import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { GET_ASSET_DEPENDENCIES_QUERY_KEY } from './useGetAssetDependencies';
import { GET_ASSET_QUERY_KEY } from './useGetAsset';

export const useResolveDependency = (options?: { onSuccess?: () => void; onError?: (error: string) => void }) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { mutate, isPending } = useMutation<
    void,
    AxiosError<{ message: string }>,
    { assetId: string; dependencyPath: string; resolvedAssetId: string }
  >({
    mutationKey: ['resolveDependency'],
    mutationFn: async ({ assetId, dependencyPath, resolvedAssetId }) => {
      await api.assets.resolveDependency(tenantId, assetId, {
        dependencyPath,
        resolvedAssetId,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_ASSET_DEPENDENCIES_QUERY_KEY],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_ASSET_QUERY_KEY],
          exact: false,
        }),
      ]);
      options?.onSuccess?.();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      options?.onError?.(error.response?.data.message ?? 'Failed to resolve dependency');
    },
  });

  return {
    resolveDependency: mutate,
    isResolving: isPending,
  };
};
